// ---------------------------------------------------------------------------
// One answer, one frame — and the frames the round closes with.
//
// Everything here draws a single thing on the stage and holds no state about
// the sequence: an answer, a player who had nothing, a host's ruling on one
// word, the best the board allowed, the solver's working, the word a conundrum
// was built from, and what the AI opponents had. `Stage` in `RevealScreen.tsx`
// picks between them; none of them knows there is a slide before or after it.
//
// They are together because they are the same job at the same size — each one
// fills the stage, each one takes `view` and at most a player and a submission,
// and each one is read against the same 390px budget the screen was measured
// for. `ReasoningNote` is here rather than in `atoms.tsx` because it is an
// answer's own annotation; the leaderboard rows draw it for the same reason
// `AnswerFrame` does.
//
// `HostRuling` has no export: both of its callers are in this file, and a
// ruling drawn anywhere other than beside the answer it is about is the banner
// this component replaced.
//
// --- What a word means -------------------------------------------------------
//
// Every word these frames put on the stage can be asked about, through
// `AskableWord`. Which ones those are is not a matter of taste: it mirrors
// `revealedWordsOfRound` in `packages/backend/src/definitions.ts`, which admits
// a live round's submissions the moment it is judged, a conundrum's solution and
// AI shadow answers with them, and the best answer with its alternatives only
// once the reveal has completed. The client draws each of those at exactly those
// moments already, so the two agree without either checking the other.
//
// **Where a frame writes the word out, the writing is the control; where it
// only sets the word in tiles, the tiles are.** A frame that does both — the
// board's best word, the word a conundrum was built from — would otherwise
// carry two controls for one word: two tab stops, two things to press, one
// answer. The tiles are the picture of the word and the line under them is the
// word itself, so the line wins. `AnswerFrame` has no such line, which is why
// its tile row is the control there and only there.
//
// The answer lands under the stage rather than in any of these frames. See the
// slot in `RevealScreen.tsx` for why: a frame is remounted by `SlideSwap` every
// time the stage changes, and this screen is exactly the viewport.
// ---------------------------------------------------------------------------

import { useMemo, useRef, useState } from 'react';
import { canBuildFromTiles, scoreLettersWord, solveNumbers } from '@countdown/shared';
import type { BestAnswer, ClientSubmission, PublicPlayer } from '@countdown/shared';
import { AudienceTag } from '../../../components/round/audience.js';
import { BlueFrame, Frame, RulingBar, TileRow, Working } from '../../../components/round/atoms.js';
import { PlayerChip } from '../../../components/primitives/PlayerChip.js';
import { listNames } from '../../../lib/format.js';
import { pickNumbersSolutions } from '../../../lib/numbersSolutions.js';
import { displayColorOf, playerById } from '../../../lib/playerLookup.js';
import { conundrumIsRaced } from './aggregate.js';
import { AskableWord, ReasonChip, STAGE_FILL } from './atoms.js';
import { answerTextOf, buzzedIn, letterTilesOf, numbersBand, rejected } from './ruling.js';
import { ReasoningModal } from './ReasoningModal.js';
import type { RevealView } from './view.js';

// --- The answer, good or ruled out -----------------------------------------
//
// **Nothing here offers the host an overrule, and that is two decisions rather
// than an omission.** A letters answer is ruled on from its row on the panel,
// where the word and the ruling are already side by side. A conundrum cannot be
// ruled on at all: the answer is any dictionary word of the full scramble length
// using every one of its letters, the engine has already asked that question,
// and `override-word` refuses a conundrum round. A button the server rejects is
// a button that promises the host something they do not have.
//
// There is no stamp over the word, and there must never be one again. A rotated
// NOT A WORD plate sat across the middle of the tiles, which meant the one
// moment a player most wants to read what they typed was the one moment they
// could not. The ruling is carried entirely by things that sit beside the word:
// the header says `claimed n` rather than `n letters`, the tiles lose the accent
// ring a good word gets, and `RulingBar` underneath names the ruling and the
// score. That component now lives in `components/round/atoms.tsx`, with the rest
// of the note.

/**
 * The host's ruling, next to the answer it is about.
 *
 * It used to be a banner at the top of the screen, which announced the ruling by
 * shoving every frame below it down the page. Here it costs one line inside the
 * frame the reader is already looking at.
 */
function HostRuling({
  view,
  player,
  submission,
}: {
  view: RevealView;
  player: PublicPlayer;
  submission: ClientSubmission;
}) {
  if (submission.hostOverride === null) return null;

  const hostName = playerById(view.state, view.state.hostId)?.displayName ?? 'The host';
  const allowed = submission.hostOverride === 'allow';
  const answer = answerTextOf(submission).toUpperCase();
  const subject = answer.length > 0 ? answer : 'the answer';

  return (
    <div className="flex shrink-0 items-center gap-3 rounded-card border border-accent/55 bg-accent/15 px-4 py-2.5">
      <span aria-hidden="true" className="h-9 w-1.5 shrink-0 rounded-full bg-accent" />
      <div className="flex flex-col gap-1">
        <span className="label-mono text-accent">host ruling</span>
        <p className="text-sm font-semibold">
          {hostName} {allowed ? 'allowed' : 'disallowed'} {subject}. {player.displayName} scores{' '}
          {allowed ? submission.score ?? 0 : 0}.
        </p>
      </div>
    </div>
  );
}

/**
 * A player who had nothing, which is an ordinary outcome rather than an absence.
 * The reveal order puts these first, and DESIGN 6.3 says they come up as a dash.
 *
 * They get a slide of their own rather than being skipped, for two reasons. The
 * cursor stepping over a non-submitter used to put nothing on screen at all, so
 * the first few beats of every reveal looked like the sequence had stalled. And
 * a round nobody answered rendered as a bare declared list, which reads as a
 * screen that failed to load rather than a round that nobody got.
 */
export function NothingFrame({ view, player }: { view: RevealView; player: PublicPlayer }) {
  return (
    <Frame className={STAGE_FILL}>
      <header className="flex shrink-0 flex-wrap items-center gap-3">
        <PlayerChip
          player={player}
          color={displayColorOf(view.state, player)}
          size="md"
          showIdentity={false}
        />
        <AudienceTag state={view.state} player={player} />
        <span className="label-mono ml-auto">
          {/* A conundrum with no buzzer has no buzz to have missed — see
              `settings.conundrumBuzzer` — so an empty seat there is the same
              ordinary nothing it is on a letters round. */}
          {view.round.type !== 'conundrum' || !conundrumIsRaced(view.state)
            ? 'nothing submitted'
            : buzzedIn(view.round, player.id)
              ? 'buzzed, no word in time'
              : 'never buzzed'}
        </span>
      </header>
      <div className="flex min-h-0 flex-1 items-center justify-center py-2">
        <span aria-hidden="true" className="text-5xl font-extrabold text-ink-dim">
          —
        </span>
      </div>
    </Frame>
  );
}

/**
 * An AI opponent's thinking, behind a chip that opens a dialog.
 *
 * The chip is all this draws. Everything about the panel — why it is portalled,
 * why there are four ways out of it, and what it was before — is on
 * `ReasoningModal`, which is where the mobile bug Isaac reported actually lived.
 *
 * Renders nothing at all unless the answer carries reasoning, which is only when
 * an LLM opponent had thinking switched on. Everyone else's frame is untouched,
 * including the header spacing. **Since the lobby control was removed no NEW
 * answer can carry it** — see `HostRowMenu` — so today this draws only on games
 * recorded while the setting still existed. That is deliberate: the reasoning is
 * stored, it was public when it was written, and hiding it now would edit the
 * record rather than stop the spending.
 */
export function ReasoningNote({
  player,
  submission,
}: {
  player: PublicPlayer;
  submission: ClientSubmission;
}) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const reasoning = submission.reasoning;
  if (!reasoning) return null;

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => setOpen(true)}
        className="label-mono shrink-0 cursor-pointer rounded-chip border border-line px-2 py-1 text-ink-dim transition-colors duration-150 ease-out hover:text-ink"
      >
        thinking
      </button>
      {open ? (
        <ReasoningModal
          player={player}
          reasoning={reasoning}
          onClose={() => {
            setOpen(false);
            // Back where it came from. The dialog is portalled to the body, so
            // without this the reveal is left with focus on nothing and the next
            // Tab starts from the top of the document.
            triggerRef.current?.focus();
          }}
        />
      ) : null}
    </>
  );
}

export function AnswerFrame({
  view,
  player,
  submission,
}: {
  view: RevealView;
  player: PublicPlayer;
  submission: ClientSubmission;
}) {
  const answer = answerTextOf(submission);
  const isRejected = rejected(submission);
  const score = submission.score ?? 0;
  // Two different failures, and a player is owed the difference. A word that is
  // spelled from the board but is not in the dictionary is a near miss; a word
  // using letters that were never there is an AI opponent hallucinating, which
  // only it can do — the engine refuses an unbuildable word from everyone else.
  //
  // Letters only. This frame also draws a conundrum answer, where `round.tiles`
  // is undefined and every wrong answer would come out as "not on the board".
  const offBoard =
    isRejected &&
    view.round.type === 'letters' &&
    answer.length > 0 &&
    !canBuildFromTiles(answer, letterTilesOf(view.round));

  return (
    <Frame className={STAGE_FILL}>
      <header className="flex shrink-0 flex-wrap items-center gap-3">
        {/* `min-w-0` so the chip gives way rather than shoving the group below
            it onto a second line. This screen is measured to fit a 390px
            viewport with nothing to spare, and the header gaining a row is
            exactly how the reveal starts scrolling again. */}
        <div className="flex min-w-0 flex-1 items-center gap-2">
          <PlayerChip
            player={player}
            color={displayColorOf(view.state, player)}
            size="md"
            showIdentity={false}
          />
          <AudienceTag state={view.state} player={player} />
        </div>
        <div className="flex shrink-0 items-center gap-3">
          <ReasoningNote player={player} submission={submission} />
          <span className={`label-mono ${isRejected ? 'text-alert' : ''}`}>
            {isRejected ? `claimed ${answer.length}` : `${answer.length} letters`}
          </span>
        </div>
      </header>

      {/* Nothing is layered over this row. Whatever the ruling, the word the
          player typed is legible.

          **The tiles are the control that asks what the word means, and this is
          the one frame where they are.** The stage reaches this for a
          conundrum's solver — a letters answer is a row on the leaderboard —
          and the answer is set in tiles and written nowhere else on it, so
          there is no text run to hang the question off. Adding one would be a
          frame saying the same word twice, which is the kind of line the frames
          below have already had cut out of them.

          The button is `w-full` and carries nothing else, so it is the same box
          the tile row already filled and the row is the same size it was. The
          affordance lands as a hairline under the answer rather than under a
          text run — which is the honest shape for it here, where the answer IS
          the row. Nothing is drawn over the tiles: a picked word is a fill
          behind them. */}
      <div className="flex min-h-0 flex-1 items-center justify-center py-2">
        <AskableWord view={view} word={answer.toUpperCase()} className="w-full">
          <TileRow
            letters={[...answer.toUpperCase()]}
            tone={isRejected ? 'rejected' : 'answer'}
            size={view.tileSize}
          />
        </AskableWord>
      </div>

      {isRejected ? (
        <RulingBar
          good={false}
          label={offBoard ? 'Not on the board' : 'Not in the dictionary'}
          score={0}
        />
      ) : (
        <RulingBar
          good
          label={view.round.type === 'conundrum' ? 'Solved it' : 'Good word'}
          score={score}
        />
      )}

      <HostRuling view={view} player={player} submission={submission} />
    </Frame>
  );
}

// --- Numbers working -------------------------------------------------------

export function NumbersAnswerFrame({
  view,
  player,
  submission,
}: {
  view: RevealView;
  player: PublicPlayer;
  submission: ClientSubmission;
}) {
  const { round } = view;
  const tiles = round.numberTiles ?? [];
  const steps = submission.steps ?? [];
  const value = submission.value ?? 0;

  return (
    <Frame className={STAGE_FILL}>
      <header className="flex shrink-0 flex-wrap items-center gap-3">
        <PlayerChip
          player={player}
          color={displayColorOf(view.state, player)}
          size="md"
          showIdentity={false}
        />
        <AudienceTag state={view.state} player={player} />
        <span className="label-mono ml-auto">
          {steps.length === 1 ? '1 step' : `${steps.length} steps`}
        </span>
      </header>

      <div className="flex min-h-0 flex-1 items-center justify-center py-2">
        <Working steps={steps} tiles={tiles} tone="plain" />
      </div>

      <RulingBar
        good={numbersBand(value, round.target ?? 0) !== 'too far out'}
        label={`${value}, ${numbersBand(value, round.target ?? 0)}`}
        score={submission.score ?? 0}
      />
      <HostRuling view={view} player={player} submission={submission} />
    </Frame>
  );
}

// --- The best the board allowed --------------------------------------------

export function BestWordFrame({ view, best }: { view: RevealView; best: BestAnswer }) {
  if (best.kind !== 'letters') return null;

  const word = best.word.toUpperCase();
  const value = scoreLettersWord(best.word, true);
  const finders = view.ordered
    .filter((player) => {
      const submission = view.submissionOf(player.id);
      return (
        submission !== undefined &&
        !rejected(submission) &&
        answerTextOf(submission).toUpperCase() === word
      );
    })
    .map((player) => player.displayName);

  return (
    <BlueFrame className={STAGE_FILL}>
      <div className="flex shrink-0 flex-wrap items-center gap-3">
        <span className="label-mono text-blue-200">the board had</span>
        <span className="ml-auto font-mono text-xs text-blue-200">
          {finders.length === 0 ? 'nobody found it' : `${listNames(finders)} found it`}
        </span>
      </div>

      <div className="flex min-h-0 flex-1 items-center justify-center py-2">
        <TileRow letters={[...word]} tone="best" size={view.tileSize} />
      </div>

      <div className="flex shrink-0 items-center gap-4 rounded-card border border-blue-400/40 bg-blue-700/30 px-4 py-3">
        <div className="flex min-w-0 flex-col gap-0.5">
          {/* The word written out, which is where the question about it is
              asked: the tiles above are the same word and making both controls
              would be two of everything for one answer. */}
          <p className="text-lg font-bold tracking-[0.04em]">
            <AskableWord view={view} word={word}>
              {word}
            </AskableWord>
          </p>
          {/* The tiles above are countable and the value is 44px to the right,
              so this line said the same two facts a third and fourth time. */}
          <p className="text-sm text-blue-200">{best.length} letters</p>
          {best.alternatives.length > 0 ? (
            <p className="truncate font-mono text-xs text-blue-200">
              {/* A colon, not a comma. The line is a label and a list, and a
                  comma read as the start of a sentence that never arrived.

                  The alternatives are askable too — the endpoint admits them
                  with the best word itself, because the round put them on this
                  slide — so the list is built out of controls rather than out of
                  `join`. The commas are kept by hand and stay outside the
                  controls, so a press lands on a word and never on punctuation.
                  `truncate` still owns the line: it is one line here or it is a
                  line that pushes the value off the frame. */}
              also there:{' '}
              {best.alternatives.slice(0, 3).map((alternative, index, shown) => {
                const other = alternative.toUpperCase();
                return (
                  <span key={other}>
                    <AskableWord view={view} word={other}>
                      {other}
                    </AskableWord>
                    {index === shown.length - 1 ? '' : ', '}
                  </span>
                );
              })}
            </p>
          ) : null}
        </div>
        <p className="tabular ml-auto text-4xl font-extrabold text-blue-200 sm:text-[44px]">
          {value}
        </p>
      </div>
    </BlueFrame>
  );
}

export function SolverFrame({ view, best }: { view: RevealView; best: BestAnswer }) {
  const tiles = view.round.numberTiles ?? [];
  const target = view.round.target;

  // The second solution is worked out HERE rather than sent down the wire.
  // `BestAnswer` carries one set of steps because that is all the room ever
  // needed; the server discards the rest of what the solver found. Rebuilding
  // them on the client costs one search on a screen that is already holding
  // still, and it keeps the payload and the engine untouched — the alternative
  // was widening a wire type so the reveal could show a second line.
  //
  // Memoised on the board, not on the render: this runs a real search and the
  // reveal re-renders on every cursor move and every clock tick.
  const picks = useMemo(() => {
    // `== null` on purpose: the field is optional as well as nullable, and a
    // round mid-selection has neither a target nor tiles yet.
    if (best.kind !== 'numbers' || target == null || tiles.length === 0) return null;
    // Small budget on purpose. The solver already found the answer server-side;
    // this pass only has to turn up ALTERNATIVE routes to it, and a reveal that
    // stutters to find a prettier sum has made a bad trade.
    const { solutions } = solveNumbers([...tiles], target, {
      maxSolutions: 60,
      timeBudgetMs: 250,
    });
    return pickNumbersSolutions(tiles, target, solutions);
  }, [best.kind, target, tiles]);

  if (best.kind !== 'numbers') return null;

  // Only when it genuinely differs. `pickNumbersSolutions` returns a null
  // `intuitive` when the roundest route IS the shortest one, which is the good
  // case and the common one — the same working under two headings is worse than
  // one heading.
  const intuitive =
    picks?.intuitive && best.exact ? picks.intuitive : null;

  return (
    <BlueFrame className={STAGE_FILL}>
      <div className="flex shrink-0 flex-wrap items-center gap-3">
        {/* Named once, whether one working is drawn or two. "the solver had,
            two ways" counted the workings out in a label, which is the one
            thing the workings themselves are already doing — two of them side
            by side say "two ways" without being told. */}
        <span className="label-mono text-blue-200">the solver</span>
        <span className="ml-auto font-mono text-xs text-blue-200">
          {best.exact ? `${best.value} exact` : `${best.value}, the closest`}
        </span>
      </div>
      <div
        className={`flex min-h-0 flex-1 items-center justify-center gap-6 py-2 ${
          intuitive ? 'flex-col min-[900px]:flex-row' : ''
        }`}
      >
        <Working steps={best.steps} tiles={tiles} tone="solver" />
        {intuitive && (
          <Working steps={intuitive.steps} tiles={tiles} tone="solver" />
        )}
      </div>
    </BlueFrame>
  );
}

/**
 * The word the conundrum was built from.
 *
 * "One that fits" and not "the answer", because the round marks any word using
 * all nine letters correct, plurals included. Calling this the answer would tell
 * a player who found a different nine and scored for it that they had got it
 * wrong. The label carries that; a sentence saying the same thing underneath
 * did not survive the read.
 */
export function ConundrumSolutionFrame({ view, best }: { view: RevealView; best: BestAnswer }) {
  if (best.kind !== 'conundrum') return null;
  const word = best.word.toUpperCase();

  return (
    <BlueFrame className={STAGE_FILL}>
      <span className="label-mono shrink-0 text-blue-200">one that fits</span>
      <div className="flex min-h-0 flex-1 items-center justify-center py-2">
        <TileRow letters={[...word]} tone="best" size={view.tileSize} />
      </div>
      {/* The line under the tiles is the control, for the reason at the head of
          this file: the tiles are the same word, and one word wants one way of
          asking about it. */}
      <p className="shrink-0 text-lg font-bold tracking-[0.04em]">
        <AskableWord view={view} word={word}>
          {word}
        </AskableWord>
      </p>
    </BlueFrame>
  );
}

/**
 * What the AI opponents had, for a conundrum only one of them could say.
 *
 * **The round destroys these and it should not.** `buzz()` refuses the second
 * buzzer outright, so a room seating four models finishes the round with three
 * answers that were computed, paid for and thrown away — and Isaac's objection
 * was the right one: comparing what several models made of the same nine letters
 * is more interesting than watching one of them press a button first.
 *
 * **Two things it will not do.** It says nothing about points, because these are
 * worth none and drawing them beside a score would invite the reading that they
 * are. And it marks a LATE answer as late rather than quietly listing it beside
 * the others: a model may take two minutes, its answer is kept because the
 * compute was bought, and "it knew the word" is a different claim from "it could
 * have won the round".
 */
export function ShadowAnswersFrame({ view }: { view: RevealView }) {
  const shadows = view.round.shadowAnswers ?? [];
  if (shadows.length === 0) return null;

  const solved = shadows.filter((shadow) => shadow.correct).length;

  return (
    <Frame dense className={STAGE_FILL}>
      <header className="flex shrink-0 flex-wrap items-baseline gap-x-3 gap-y-1">
        <h2 className="text-lg font-bold sm:text-xl">What the AI had</h2>
        {/* The count says it once. A tick per row would say it n times. */}
        <span className="label-mono">
          {solved} of {shadows.length} right
        </span>
      </header>
      <p className="shrink-0 text-[13px] text-ink-dim">
        {/* True only while there is a buzzer. With `conundrumBuzzer` off every
            model's answer was played and scored like anybody else's, so the
            sentence would be describing a rule the room turned off — and the
            slide would be apologising for answers that were not wasted. */}
        {conundrumIsRaced(view.state)
          ? 'Only one buzz counts, so most of these were never played.'
          : 'What each AI made of the same nine letters.'}
      </p>

      <ul aria-label="What each AI worked out" className="flex flex-col gap-1.5">
        {shadows.map((shadow) => {
          const player = playerById(view.state, shadow.playerId);
          const answer = shadow.answer.toUpperCase();
          const played = view.round.submissions[shadow.playerId] !== undefined;

          return (
            <li
              key={shadow.playerId}
              data-shadow-player={shadow.playerId}
              className="flex items-center gap-3 rounded-card border border-transparent bg-surface-2 px-3 py-2"
            >
              <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                {player === null ? (
                  <span className="text-[13px] font-semibold text-ink-dim">A seat that left</span>
                ) : (
                  <PlayerChip
                    player={player}
                    color={displayColorOf(view.state, player)}
                    size="sm"
                    showIdentity={false}
                  />
                )}
                <div className="flex min-w-0 flex-wrap items-center gap-1">
                  {/* Askable, and the round is what makes that true rather than
                      this list: `revealedWordsOfRound` admits a judged
                      conundrum's shadow answers exactly as it admits its
                      submissions. This is the one slide in the game where
                      several models answer the same question, so "what did that
                      one even mean" is the question it exists to provoke — and a
                      word that was not a fit is still a word somebody may want
                      defined, which is most of the point of showing it. */}
                  {answer.length === 0 ? (
                    <span className="min-w-0 truncate text-[15px] font-extrabold tracking-[0.06em] text-ink-dim">
                      —
                    </span>
                  ) : (
                    <AskableWord
                      view={view}
                      word={answer}
                      className={`min-w-0 truncate text-[15px] font-extrabold tracking-[0.06em] ${
                        shadow.correct ? 'text-ink' : 'text-ink-dim'
                      }`}
                    >
                      {answer}
                    </AskableWord>
                  )}
                  {shadow.correct ? null : (
                    <ReasonChip tone="alert">not a fit</ReasonChip>
                  )}
                  {played ? <ReasonChip tone="dim">played it</ReasonChip> : null}
                  {shadow.landedInTime ? null : (
                    // Kept, but never counted as a solve it could have had.
                    <ReasonChip tone="dim">after the round</ReasonChip>
                  )}
                </div>
              </div>
              <span className="tabular shrink-0 font-mono text-xs text-ink-dim">
                {(shadow.latencyMs / 1000).toFixed(1)}s
              </span>
            </li>
          );
        })}
      </ul>
    </Frame>
  );
}
